"use client";

import { usePostHog } from "posthog-js/react";
import { useCallback } from "react";

import {
  ChurnIndicators,
  EventProperties,
  FeatureEvent,
  FeatureFlag,
  FunnelEvent,
  KnownFlagKey,
  KNOWN_FLAGS,
  RetentionEvent,
  UserEvent,
  UserProperties,
} from "@/types/analytics";
import {
  calculateChurnIndicators,
  getChurnFactors,
  isUserAtRisk,
  isUserChurned,
} from "@/utils/churnDetection";
import { generateEventKey, isDeduplicated } from "@/utils/eventDeduplication";
import { usePerformanceMonitoring } from "@/utils/performanceMonitoring";

export function useAnalytics() {
  const perfMonitoring = usePerformanceMonitoring();
  const posthog = usePostHog();

  const trackFunnelEvent = useCallback(
    (event: FunnelEvent, properties?: EventProperties) => {
      if (!posthog) return;
      perfMonitoring.trackEvent(event);
      posthog.capture(event, properties || {});
    },
    [posthog, perfMonitoring],
  );

  const trackUserEvent = useCallback(
    (event: UserEvent, properties?: EventProperties) => {
      if (!posthog) return;
      posthog.capture(event, properties || {});
    },
    [posthog],
  );

  const trackRetentionEvent = useCallback(
    (event: RetentionEvent, properties?: EventProperties) => {
      if (!posthog) return;
      posthog.capture(event, properties || {});
    },
    [posthog],
  );

  const trackFeatureEvent = useCallback(
    (featureName: string, properties?: EventProperties) => {
      if (!posthog) return;
      posthog.capture(FeatureEvent.FEATURE_USED, {
        featureName,
        ...properties,
      });
    },
    [posthog],
  );

  const setUserProperties = useCallback(
    (properties: UserProperties) => {
      if (!posthog) return;
      posthog.people.set(properties);
    },
    [posthog],
  );

  const identifyUser = useCallback(
    (email: string, properties?: Partial<UserProperties>) => {
      if (!posthog) return;
      posthog.identify(email);
      if (properties) {
        posthog.people.set(properties);
      }
    },
    [posthog],
  );

  const resetUser = useCallback(() => {
    if (!posthog) return;
    posthog.reset();
  }, [posthog]);

  const trackFormProgress = useCallback(
    (
      step: number,
      totalSteps: number,
      formName: string = "request_form",
      properties?: EventProperties,
    ) => {
      if (!posthog) return;
      posthog.capture(FunnelEvent.REQUEST_FORM_STEP_STARTED, {
        formName,
        formStep: step,
        totalSteps,
        completionPercentage: Math.round((step / totalSteps) * 100),
        ...properties,
      });
    },
    [posthog],
  );

  const trackFormFieldInteraction = useCallback(
    (
      fieldName: string,
      fieldType: "focused" | "filled" | "error",
      formName: string = "request_form",
      properties?: EventProperties,
    ) => {
      if (!posthog) return;
      const eventMap = {
        focused: UserEvent.FORM_FIELD_FOCUSED,
        filled: UserEvent.FORM_FIELD_FILLED,
        error: UserEvent.FORM_FIELD_ERROR,
      };

      posthog.capture(eventMap[fieldType], {
        formName,
        formField: fieldName,
        ...properties,
      });
    },
    [posthog],
  );

  const trackFormSubmission = useCallback(
    (
      success: boolean,
      formName: string = "request_form",
      properties?: EventProperties,
    ) => {
      if (!posthog) return;
      if (success) {
        posthog.capture(FunnelEvent.REQUEST_FORM_SUBMITTED, {
          formName,
          submissionSuccess: true,
          ...properties,
        });
        posthog.capture(FunnelEvent.REQUEST_SUBMISSION_SUCCESS, {
          formName,
          ...properties,
        });
      } else {
        posthog.capture(FunnelEvent.REQUEST_SUBMISSION_FAILED, {
          formName,
          submissionSuccess: false,
          ...properties,
        });
      }
    },
    [posthog],
  );

  const trackFormAbandonment = useCallback(
    (
      step: number,
      totalSteps: number,
      timeSpent?: number,
      formName: string = "request_form",
      properties?: EventProperties,
    ) => {
      if (!posthog) return;
      posthog.capture(FunnelEvent.REQUEST_FORM_ABANDONED, {
        formName,
        formStep: step,
        totalSteps,
        abandonmentPercentage: Math.round((step / totalSteps) * 100),
        timeSpentSeconds: timeSpent,
        ...properties,
      });
    },
    [posthog],
  );

  const trackError = useCallback(
    (
      errorCode: string,
      errorMessage: string,
      errorType: string = "unknown",
      properties?: EventProperties,
    ) => {
      if (!posthog) return;
      posthog.capture(UserEvent.ERROR_ENCOUNTERED, {
        errorCode,
        errorMessage,
        errorType,
        timestamp: new Date().toISOString(),
        ...properties,
      });
    },
    [posthog],
  );

  const trackPageView = useCallback(
    (pageName: string, properties?: EventProperties) => {
      if (!posthog) return;
      const key = generateEventKey("$pageview", { formName: pageName });
      if (isDeduplicated(key)) return;
      posthog.capture("$pageview", { pageName, ...properties });
    },
    [posthog],
  );

  const trackChurnIndicators = useCallback(
    (indicators: ChurnIndicators, userId: string) => {
      if (!posthog) return;
      posthog.people.set({
        daysSinceLastVisit: indicators.daysSinceLastVisit,
        formCompletionRate: indicators.formCompletionRate,
        totalSessions: indicators.totalSessions,
        churnProbability: indicators.churnProbability,
        churnReason: indicators.churnReason,
      });

      posthog.capture(RetentionEvent.USER_ENGAGED, {
        userId,
        churnProbability: indicators.churnProbability,
        formCompletionRate: indicators.formCompletionRate,
        totalPageViews: indicators.totalPageViews,
        averageSessionDuration: indicators.averageSessionDuration,
      });
    },
    [posthog],
  );

  const trackSessionStart = useCallback(() => {
    if (!posthog) return;
    posthog.capture(RetentionEvent.USER_SESSION_STARTED, {
      timestamp: new Date().toISOString(),
    });
  }, [posthog]);

  const trackSessionEnd = useCallback(
    (sessionDuration: number) => {
      if (!posthog) return;
      posthog.capture(RetentionEvent.USER_SESSION_ENDED, {
        sessionDurationSeconds: sessionDuration,
        timestamp: new Date().toISOString(),
      });
    },
    [posthog],
  );

  const trackUserReturn = useCallback(() => {
    if (!posthog) return;
    posthog.capture(RetentionEvent.USER_RETURNED, {
      timestamp: new Date().toISOString(),
    });
  }, [posthog]);

  const trackChurn = useCallback(
    (reason?: string, properties?: EventProperties) => {
      if (!posthog) return;
      posthog.capture(RetentionEvent.USER_CHURNED, {
        churnReason: reason,
        timestamp: new Date().toISOString(),
        ...properties,
      });
      posthog.people.set({
        conversionStage: "churned",
        churnDate: new Date().toISOString(),
        churnReason: reason,
      });
    },
    [posthog],
  );

  const getFeatureFlag = useCallback(
    (flagKey: string | KnownFlagKey): FeatureFlag | null => {
      if (!posthog) return null;

      try {
        const enabled = posthog.getFeatureFlag(flagKey);
        const payload = posthog.getFeatureFlagPayload(flagKey);

        const flagValue: unknown = payload || undefined;
        const isValidFlagValue =
          typeof flagValue === "string" ||
          typeof flagValue === "number" ||
          typeof flagValue === "boolean" ||
          (flagValue &&
            typeof flagValue === "object" &&
            !Array.isArray(flagValue)) ||
          flagValue === null ||
          flagValue === undefined;

        return {
          key: flagKey,
          enabled: enabled === true,
          value: isValidFlagValue ? (flagValue as never) : undefined,
          variant: payload
            ? {
                key: String(payload),
                isActive: true,
                value: isValidFlagValue ? (flagValue as never) : undefined,
              }
            : undefined,
        };
      } catch (error) {
        console.error(`Failed to get feature flag "${flagKey}":`, error);
        return null;
      }
    },
    [posthog],
  );

  const isFeatureEnabled = useCallback(
    (flagKey: string | KnownFlagKey): boolean => {
      if (!posthog) return false;

      try {
        const result = posthog.getFeatureFlag(flagKey);
        return result === true;
      } catch (error) {
        console.error(`Failed to check feature flag "${flagKey}":`, error);
        return false;
      }
    },
    [posthog],
  );

  const getFeatureFlagVariant = useCallback(
    (flagKey: string | KnownFlagKey): string | null => {
      if (!posthog) return null;

      try {
        const variant = posthog.getFeatureFlagPayload(flagKey);
        return variant ? String(variant) : null;
      } catch (error) {
        console.error(`Failed to get variant for flag "${flagKey}":`, error);
        return null;
      }
    },
    [posthog],
  );

  const trackFeatureUsage = useCallback(
    (
      flagKey: string | KnownFlagKey,
      properties?: Omit<EventProperties, "flagKey" | "flagEnabled">,
    ) => {
      if (!posthog) return;

      try {
        const enabled = posthog.getFeatureFlag(flagKey);
        const variant = posthog.getFeatureFlagPayload(flagKey);

        posthog.capture(FeatureEvent.FEATURE_USED, {
          flagKey,
          flagEnabled: enabled === true,
          flagVariant: variant ? String(variant) : undefined,
          ...properties,
        });
      } catch (error) {
        console.error(`Failed to track feature usage for "${flagKey}":`, error);
      }
    },
    [posthog],
  );

  const trackFeatureFirstUse = useCallback(
    (
      flagKey: string | KnownFlagKey,
      properties?: Omit<EventProperties, "flagKey" | "isFirstUse">,
    ) => {
      if (!posthog) return;

      try {
        const enabled = posthog.getFeatureFlag(flagKey);
        const variant = posthog.getFeatureFlagPayload(flagKey);

        posthog.capture(FeatureEvent.FEATURE_FIRST_USE, {
          flagKey,
          isFirstUse: true,
          flagEnabled: enabled === true,
          flagVariant: variant ? String(variant) : undefined,
          ...properties,
        });
      } catch (error) {
        console.error(`Failed to track first use for "${flagKey}":`, error);
      }
    },
    [posthog],
  );

  const getMultipleFlags = useCallback(
    (
      flagKeys: (string | KnownFlagKey)[],
    ): Record<string, FeatureFlag | null> => {
      if (!posthog) return {};

      return flagKeys.reduce(
        (acc, key) => {
          acc[key] = getFeatureFlag(key);
          return acc;
        },
        {} as Record<string, FeatureFlag | null>,
      );
    },
    [posthog, getFeatureFlag],
  );

  const getEnabledFlags = useCallback(
    (flagKeys?: (string | KnownFlagKey)[]): (string | KnownFlagKey)[] => {
      if (!posthog) return [];

      const keysToCheck = flagKeys || Object.values(KNOWN_FLAGS);
      return keysToCheck.filter((key) => isFeatureEnabled(key));
    },
    [posthog, isFeatureEnabled],
  );

  const reloadFeatureFlags = useCallback(async () => {
    if (!posthog) return;

    try {
      await new Promise<void>((resolve) => {
        posthog.reloadFeatureFlags();
        const timeout = setTimeout(resolve, 3000);
        const checkInterval = setInterval(() => {
          resolve();
          clearInterval(checkInterval);
          clearTimeout(timeout);
        }, 100);
      });
    } catch (error) {
      console.error("Failed to reload feature flags:", error);
    }
  }, [posthog]);

  const detectUserChurn = useCallback(
    (userIndicators: Partial<ChurnIndicators>): ChurnIndicators => {
      try {
        return calculateChurnIndicators(userIndicators);
      } catch (error) {
        console.error("Failed to detect user churn:", error);
        return {
          daysSinceLastVisit: 0,
          daysSinceLastFormSubmission: 0,
          totalPageViews: 0,
          totalFormAttempts: 0,
          formCompletionRate: 0,
          averageSessionDuration: 0,
          totalSessions: 0,
          hasCompletedForm: false,
          hasVisitedMultiplePages: false,
          hasShownInterest: false,
          churnProbability: "low",
        };
      }
    },
    [],
  );

  const isUserChurnedCheck = useCallback(
    (indicators: ChurnIndicators): boolean => {
      return isUserChurned(indicators);
    },
    [],
  );

  const isUserRiskCheck = useCallback(
    (indicators: ChurnIndicators): boolean => {
      return isUserAtRisk(indicators);
    },
    [],
  );

  const getChurnFactorsData = useCallback((indicators: ChurnIndicators) => {
    try {
      return getChurnFactors(indicators);
    } catch (error) {
      console.error("Failed to get churn factors:", error);
      return {
        recencyScore: 0,
        frequencyScore: 0,
        monetaryScore: 0,
        engagementScore: 0,
        conversionScore: 0,
        sessionHealthScore: 0,
      };
    }
  }, []);

  const trackChurnPrediction = useCallback(
    (indicators: ChurnIndicators, userId: string) => {
      if (!posthog) return;

      const churnFactors = getChurnFactors(indicators);

      posthog.capture(RetentionEvent.USER_INACTIVE, {
        userId,
        churnProbability: indicators.churnProbability,
        churnReason: indicators.churnReason,
        recencyScore: churnFactors.recencyScore,
        frequencyScore: churnFactors.frequencyScore,
        monetaryScore: churnFactors.monetaryScore,
        engagementScore: churnFactors.engagementScore,
        conversionScore: churnFactors.conversionScore,
        sessionHealthScore: churnFactors.sessionHealthScore,
        daysSinceLastVisit: indicators.daysSinceLastVisit,
        totalSessions: indicators.totalSessions,
        formCompletionRate: indicators.formCompletionRate,
      });

      posthog.people.set({
        churnProbability: indicators.churnProbability,
        churnReason: indicators.churnReason,
        predictedChurnDate:
          indicators.churnProbability === "high"
            ? new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
            : null,
      });
    },
    [posthog],
  );

  return {
    trackFunnelEvent,
    trackUserEvent,
    trackRetentionEvent,
    trackFeatureEvent,
    trackError,
    trackPageView,
    trackFormProgress,
    trackFormFieldInteraction,
    trackFormSubmission,
    trackFormAbandonment,
    identifyUser,
    setUserProperties,
    resetUser,
    trackChurnIndicators,
    trackSessionStart,
    trackSessionEnd,
    trackUserReturn,
    trackChurn,
    getFeatureFlag,
    isFeatureEnabled,
    getFeatureFlagVariant,
    trackFeatureUsage,
    trackFeatureFirstUse,
    getMultipleFlags,
    getEnabledFlags,
    reloadFeatureFlags,
    detectUserChurn,
    isUserChurnedCheck,
    isUserRiskCheck,
    getChurnFactorsData,
    trackChurnPrediction,
    getPerformanceMetrics: perfMonitoring.getMetrics,
    getApiMetrics: perfMonitoring.getApiMetrics,
    getHealthStatus: perfMonitoring.getHealthStatus,
    getEventFrequencies: perfMonitoring.getEventFrequencies,
  };
}
